/**
 * t259u: one tap shouldn't take a person (or a team, a member, a link) off something. Places that remove straight
 * away ask first with a short "Remove Jane from this task?" (Cancel / Remove, Remove in danger style). Pickers that
 * only change a draft before you save don't ask.
 */
import { ask } from '@/components/ui';

/** What was in `before` and isn't in `after`. */
export const removedFrom = <T,>(before: readonly T[], after: readonly T[]): T[] => before.filter(x => !after.includes(x));

/** "Remove Jane from this task?", "Remove Jane and Tom from …", "Remove 3 from …". */
export function removalTitle(names: string[], from: string): string {
  return `Remove ${names.length > 3 ? `${names.length} people` : nameList(names)} from ${from}?`;
}

export function confirmRemoval(names: string[], from: string, body?: string): Promise<boolean> {
  return ask({ title: removalTitle(names, from), body, confirmLabel: 'Remove', danger: true });
}

/** "Jane", "Jane and Tom", "Jane, Tom and Sam". */
export function nameList(names: string[]): string {
  return names.length <= 1 ? names[0] ?? '' : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * t261u: what a change of assignees does. Adding to an empty list, or adding while keeping everyone, needs no
 * confirmation; taking someone off is a removal; taking someone off while adding someone else is a reassignment.
 */
export function assigneeChange<T>(before: readonly T[], after: readonly T[]): { kind: 'none' | 'remove' | 'reassign'; removed: T[]; added: T[] } {
  const removed = removedFrom(before, after), added = removedFrom(after, before);
  return { kind: !removed.length ? 'none' : added.length ? 'reassign' : 'remove', removed, added };
}

/** "Reassign from Jane to Tom?" / "Reassign from Jane to Tom and Sam?" with Cancel / Reassign. */
export function reassignTitle(from: string[], to: string[]): string {
  return `Reassign from ${nameList(from)} to ${nameList(to)}?`;
}
export function confirmReassign(from: string[], to: string[], body?: string): Promise<boolean> {
  return ask({ title: reassignTitle(from, to), body, confirmLabel: 'Reassign', danger: false });
}

/** Confirm whatever a change of assignees needs (nothing, a removal or a reassignment); true to go ahead. */
/**
 * `recentlyAdded`: people (or teams) added a moment ago in the same picker. Pickers that save each change make a swap
 * two steps (add Tom, then take Jane off), so taking Jane off after adding Tom reads as the reassignment it is.
 */
export function confirmAssigneeChange(before: string[], after: string[], nameOf: (id: string) => string, from: string, recentlyAdded: readonly string[] = []): Promise<boolean> {
  const c = assigneeChange(before, after);
  if (c.kind === 'none') return Promise.resolve(true);
  const swappedIn = recentlyAdded.filter(id => after.includes(id) && !c.removed.includes(id));
  if (c.kind === 'remove' && swappedIn.length) return confirmReassign(c.removed.map(nameOf), swappedIn.map(nameOf));
  if (c.kind === 'remove') return confirmRemoval(c.removed.map(nameOf), from);
  return confirmReassign(c.removed.map(nameOf), c.added.map(nameOf));
}

/**
 * t261u: one-person slots (a shift, an issue). Nobody there before, or the same person: no question. Someone there
 * replaced by someone else: "Reassign from Jane to Tom?". Someone there taken off: "Remove Jane from this shift?".
 */
export function confirmSingleAssignee(fromId: string | null | undefined, toId: string | null | undefined, nameOf: (id: string) => string, from: string): Promise<boolean> {
  if (!fromId || fromId === toId) return Promise.resolve(true);
  return toId ? confirmReassign([nameOf(fromId)], [nameOf(toId)]) : confirmRemoval([nameOf(fromId)], from);
}
